import React from "react";
import {
  Paperclip,
  Images,
  Sticker,
  Camera,
  UserRound,
  MapPin,
  Type,
  Lock,
  PenLine,
  Check,
  Plus,
} from "lucide-react";

import { Button } from "../ui/button";
import { Popover, PopoverTrigger, PopoverContent } from "../ui/popover";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { cn } from "../../lib/utils";

// O "+" da barra de digitação.
//
// Antes eram nove ícones soltos numa fita: em painel largo tomavam 662px e
// espremiam o campo de texto, e abaixo de 1024px viravam um "⋮" com outra
// lista, escrita à parte — duas manutenções para o mesmo menu. Agora é um
// único botão em qualquer largura, com o menu dividido em duas colunas: o que
// se ANEXA à conversa de um lado, o que muda o JEITO de escrever do outro.

// Uma linha do menu. `ativo` acende os itens que são chave liga/desliga, e
// `detalhe` é a segunda linha em cinza (ex.: "Não visível ao cliente").
const Item = React.forwardRef(
  (
    { icone: Icone, rotulo, detalhe, ativo, desabilitado, destaque, ...props },
    ref,
  ) => (
    <button
      ref={ref}
      type="button"
      disabled={desabilitado}
      className={cn(
        "flex w-full gap-3 rounded-lg px-2.5 py-2 text-left transition-colors",
        // Sem segunda linha o ícone fica centrado com o rótulo; com ela,
        // alinhado ao topo — senão desce para o meio do bloco de duas linhas
        // e deixa a coluna de ícones irregular.
        detalhe ? "items-start" : "items-center",
        "hover:bg-accent focus-visible:bg-accent focus-visible:outline-hidden",
        "disabled:pointer-events-none disabled:opacity-40",
      )}
      {...props}
    >
      <Icone
        className={cn(
          "h-[18px] w-[18px] shrink-0 text-muted-foreground",
          detalhe && "mt-0.5",
          ativo && (destaque === "aviso" ? "text-amber-500" : "text-primary"),
        )}
      />
      <span className="min-w-0 flex-1">
        <span
          className={cn(
            "block truncate text-sm leading-tight",
            ativo &&
              (destaque === "aviso"
                ? "font-medium text-amber-600 dark:text-amber-400"
                : "font-medium text-primary"),
          )}
        >
          {rotulo}
        </span>
        {detalhe && (
          <span className="mt-0.5 block truncate text-xs leading-tight text-muted-foreground">
            {detalhe}
          </span>
        )}
      </span>
      {ativo && (
        <Check
          className={cn(
            "h-4 w-4 shrink-0",
            detalhe && "mt-0.5",
            destaque === "aviso" ? "text-amber-500" : "text-primary",
          )}
        />
      )}
    </button>
  ),
);
Item.displayName = "ItemDoMenuDeAcoes";

const MenuDeAcoes = ({
  desabilitado,
  idDoCampoDeArquivo,
  aoAbrirBiblioteca,
  aoAbrirFigurinhas,
  aoAbrirCamera,
  aoAbrirContato,
  aoAbrirLocalizacao,
  formatacaoAberta,
  aoAlternarFormatacao,
  privado,
  aoAlternarPrivado,
  signMessage,
  aoAlternarAssinatura,
  nomeDeAssinatura,
  opcoesDeAlias = [],
  aliasId,
  aoTrocarAlias,
  nomeDoCadastro,
}) => {
  const [aberto, setAberto] = React.useState(false);

  const semAlias = opcoesDeAlias.length === 0;

  // Fecha o menu depois de escolher: a ação abre um modal ou muda a barra, e
  // o painel aberto por cima só atrapalharia.
  const escolher = (acao) => () => {
    setAberto(false);
    acao?.();
  };

  return (
    <Popover open={aberto} onOpenChange={setAberto}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          disabled={desabilitado}
          aria-label="Mais ações"
          title="Anexar, formatar, assinar"
          className="h-9 w-9 shrink-0 rounded-full bg-muted/70 text-muted-foreground hover:bg-muted"
        >
          <Plus
            className={cn(
              "h-5 w-5 transition-transform",
              aberto && "rotate-45",
            )}
          />
        </Button>
      </PopoverTrigger>

      <PopoverContent
        align="start"
        sideOffset={10}
        // Duas colunas em qualquer largura, como no desenho aprovado; num
        // celular estreito elas viram uma só para o rótulo não quebrar.
        className="w-[min(30rem,calc(100vw-1.5rem))] p-2"
      >
        <div className="grid grid-cols-1 gap-1 sm:grid-cols-2 sm:divide-x sm:divide-border">
          <div className="space-y-0.5 sm:pr-2">
            {/* O input de arquivo mora na barra; aqui só o rótulo que o
                dispara, para o clique abrir o seletor do sistema. */}
            <Item
              icone={Paperclip}
              rotulo="Anexo"
              desabilitado={desabilitado}
              onClick={escolher(() =>
                document.getElementById(idDoCampoDeArquivo)?.click(),
              )}
            />
            <Item
              icone={Images}
              rotulo="Biblioteca de imagens"
              desabilitado={desabilitado}
              onClick={escolher(aoAbrirBiblioteca)}
            />
            <Item
              icone={Sticker}
              rotulo="Figurinhas"
              desabilitado={desabilitado}
              onClick={escolher(aoAbrirFigurinhas)}
            />
            <Item
              icone={Camera}
              rotulo="Câmera"
              desabilitado={desabilitado}
              onClick={escolher(aoAbrirCamera)}
            />
            <Item
              icone={UserRound}
              rotulo="Contato"
              desabilitado={desabilitado}
              onClick={escolher(aoAbrirContato)}
            />
            <Item
              icone={MapPin}
              rotulo="Localização"
              desabilitado={desabilitado}
              onClick={escolher(aoAbrirLocalizacao)}
            />
          </div>

          <div className="space-y-0.5 border-t pt-1 sm:border-t-0 sm:pl-2 sm:pt-0">
            <Item
              icone={Type}
              rotulo="Formatar texto"
              detalhe="Negrito, itálico, link"
              ativo={formatacaoAberta}
              desabilitado={desabilitado}
              onClick={escolher(aoAlternarFormatacao)}
            />
            <Item
              icone={Lock}
              rotulo="Mensagem privada"
              detalhe="Não visível ao cliente"
              ativo={privado}
              destaque="aviso"
              desabilitado={desabilitado}
              onClick={escolher(aoAlternarPrivado)}
            />
            <Item
              icone={PenLine}
              rotulo="Assinatura / Nome"
              detalhe={
                signMessage
                  ? `Responder como ${nomeDeAssinatura}`
                  : "Responder sem assinar"
              }
              ativo={signMessage}
              desabilitado={desabilitado}
              // Não fecha o menu: quem liga a assinatura costuma querer
              // escolher o nome logo em seguida.
              onClick={() => aoAlternarAssinatura(!signMessage)}
            />

            {/* O nome com que a mensagem sai. Antes só aparecia quando a
                assinatura já estava ligada, escondido dentro do item acima —
                quem quisesse trocar de nome tinha que descobrir que precisava
                ligar a assinatura primeiro. Agora é um item próprio do menu,
                como no desenho aprovado. */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild disabled={semAlias}>
                <Item
                  icone={UserRound}
                  rotulo="Alias"
                  detalhe={
                    semAlias
                      ? "Cadastre em Usuários › Nomes alternativos"
                      : `Escolher nome — ${nomeDeAssinatura}`
                  }
                  desabilitado={desabilitado || semAlias}
                />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="max-w-[16rem]">
                <DropdownMenuItem onSelect={() => aoTrocarAlias(null)}>
                  {aliasId ? "" : "✓ "}
                  {nomeDoCadastro} (cadastro)
                </DropdownMenuItem>
                {opcoesDeAlias.map((alias) => (
                  <DropdownMenuItem
                    key={alias.id}
                    // Escolher um nome só faz efeito com a assinatura ligada;
                    // ligar junto evita o "troquei o nome e saiu sem nome".
                    onSelect={() => {
                      aoTrocarAlias(alias.id);
                      if (!signMessage) aoAlternarAssinatura(true);
                    }}
                  >
                    {aliasId === alias.id ? "✓ " : ""}
                    {alias.name}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default MenuDeAcoes;
